import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams, useNavigationType } from "react-router-dom";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TabbarItem from "components/community/tabbar_item";
import {
  getCommunityCount,
  getCommunityData,
} from "firebase_/firebase_function/firebase_community";
import { getBlockData } from "firebase_/firebase_function/firebase_user";
import Feed from "components/community/feed";
import { useUser } from "util/context/user_context";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
};

const TAB_LABELS = ["이야기", "고장문의"];
const ITEMS_PER_PAGE = 4;
const PAGE_GROUP = 5;

function tabToType(tab) {
  return tab === "고장문의" ? "고장 문의" : "농사 이야기";
}

function SkeletonCard() {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: 20,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
      }}
    >
      <div
        style={{
          width: 112,
          height: 112,
          borderRadius: 12,
          background: "#F3F4F6",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
        <div style={{ height: 12, width: "30%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 18, width: "70%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "60%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "50%", background: "#F3F4F6", borderRadius: 6 }} />
      </div>
    </div>
  );
}

function EmptyState({ onWrite }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 20px",
        background: "#fff",
        borderRadius: 16,
        border: `1px dashed ${C.line}`,
        gap: 12,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 9999,
          background: C.greenBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 34,
        }}
      >
        ✏️
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>
        아직 작성된 글이 없어요
      </div>
      <div style={{ fontSize: 13, color: C.sub, textAlign: "center" }}>
        첫 번째 글의 주인공이 되어보세요.
      </div>
      <button
        type="button"
        onClick={onWrite}
        style={{
          marginTop: 6,
          padding: "10px 18px",
          borderRadius: 9999,
          background: C.greenDark,
          color: "#fff",
          fontSize: 14,
          fontWeight: 700,
          border: "none",
          cursor: "pointer",
        }}
      >
        + 글쓰기
      </button>
    </div>
  );
}

function SimplePagenation({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const startPage = Math.floor((page - 1) / PAGE_GROUP) * PAGE_GROUP + 1;
  const endPage = Math.min(startPage + PAGE_GROUP - 1, totalPages);
  const numbers = [];
  for (let i = startPage; i <= endPage; i++) numbers.push(i);

  const canPrev = page > 1;
  const canNext = page < totalPages;
  const btnBase = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    fontSize: 14,
  };

  return (
    <div style={{ display: "flex", gap: 20, margin: "24px 0", justifyContent: "center", alignItems: "center" }}>
      <button
        type="button"
        onClick={() => canPrev && onChange(page - 1)}
        style={{ ...btnBase, cursor: canPrev ? "pointer" : "default", color: canPrev ? C.sub : "#c3c3c3" }}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        <span>이전</span>
      </button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {numbers.map((n) => {
          const active = n === page;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              style={{
                width: 32,
                height: 32,
                cursor: "pointer",
                borderRadius: 9999,
                backgroundColor: active ? C.green : "transparent",
                color: active ? "#fff" : C.body,
                fontWeight: active ? 700 : 500,
                border: "none",
                transition: "all 0.15s ease",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => canNext && onChange(page + 1)}
        style={{ ...btnBase, cursor: canNext ? "pointer" : "default", color: canNext ? C.sub : "#c3c3c3" }}
      >
        <span>다음</span>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
}

export default function DesktopCommunityScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userData } = useUser();

  const currentTab = searchParams.get("category") || "이야기";
  const type = tabToType(currentTab);

  const [blockList, setBlockList] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(() => {
    if (navigationType === "POP") {
      return Number(sessionStorage.getItem(`page_${location.pathname}`) ?? 1);
    }
    return 1;
  });

  // 차단 목록 + 총 개수는 처음 한 번만 (탭별로는 새로 고침)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const block = userData?.docId ? await getBlockData(userData.docId) : [];
        if (cancelled) return;
        setBlockList(block || []);
      } catch {
        setBlockList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.docId]);

  // 탭/차단 변경 시 데이터 + count
  useEffect(() => {
    if (blockList === null) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [count, items] = await Promise.all([
          getCommunityCount(type, blockList),
          // 페이지 그룹 단위(5페이지=20개)씩 안전하게 가져온다.
          getCommunityData(type, ITEMS_PER_PAGE * PAGE_GROUP, "", blockList),
        ]);
        if (cancelled) return;
        setTotalCount(count || 0);
        setAllItems(Array.isArray(items) ? items : []);
      } catch {
        if (!cancelled) {
          setTotalCount(0);
          setAllItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [type, blockList]);

  // 다른 탭으로 옮길 때 페이지 1로
  useEffect(() => {
    setPage(1);
  }, [type]);

  useEffect(() => {
    sessionStorage.setItem(`page_${location.pathname}`, page);
    window.scrollTo({ top: 0 });
  }, [page, location.pathname]);

  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
  const currentItems = useMemo(() => {
    return allItems.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  }, [allItems, page]);

  const onSelectTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set("category", tab);
    setSearchParams(next);
  };

  const onWrite = () => navigate("/community/write");

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        maxWidth: 1100,
        margin: "0 auto",
        padding: "32px 20px 80px",
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            커뮤니티
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            농가들과 정보를 나누고 궁금증을 해결하세요.
          </div>
        </div>
        <button
          type="button"
          onClick={onWrite}
          style={{
            padding: "10px 18px",
            borderRadius: 10,
            background: C.greenDark,
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          + 글쓰기
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {TAB_LABELS.map((t) => (
          <TabbarItem key={t} currentTab={currentTab} item={t} onClick={() => onSelectTab(t)} />
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : currentItems.length > 0 ? (
          currentItems.map((story, idx) => (
            <Feed key={story.docId || idx} story={story} />
          ))
        ) : (
          <EmptyState onWrite={onWrite} />
        )}
      </div>

      <SimplePagenation page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}
